import React, { useState, useEffect } from 'react';
import { ShoppingBag, ExternalLink, ChevronDown, ChevronUp, ChevronRight, Flame } from 'lucide-react';
import { getRecommendations } from '../data/recommendations';
import { useFeatureFlag } from '../context/FeatureFlagsContext';

/**
 * GearRecommendations — affiliate gear card, collapsed by default.
 *
 * Props:
 *   groupKey      - key into recommendationGroups in data/recommendations.js
 *   customGroup   - pass a group object directly instead of using groupKey
 *   defaultOpen   - start expanded (default false)
 *   forceOpen     - controlled: when true, opens the card imperatively (e.g. from popup CTA)
 *   accent        - unused legacy prop, kept for compatibility
 *
 * Items with `urgent: true` get a flame icon + accent border + "Start here" badge.
 * Items with `subItems` get an in-row accordion to browse individual product options.
 *
 * Gated behind the `affiliates` feature flag — renders nothing until flipped on
 * in Fire Station (flip per partner approval).
 */
const GearRecommendations = ({ groupKey, customGroup, defaultOpen = false, forceOpen = false, accent = 'enf-accent' }) => {
  const { enabled: affiliatesEnabled } = useFeatureFlag('affiliates');
  const group = customGroup || getRecommendations(groupKey);
  const [open, setOpen] = useState(defaultOpen);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  if (!affiliatesEnabled) return null;
  if (!group) return null;

  const toggleItem = (id) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full p-4 flex items-center gap-3 hover:bg-enf-surface-mid/40 transition-colors">
        <div className="w-9 h-9 rounded-md bg-enf-surface-special/50 border border-enf-border-special/30 flex items-center justify-center flex-shrink-0">
          <ShoppingBag className="w-4 h-4 text-enf-role-info" />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-enf-role-info font-semibold">Recommended gear</span>
            <span className="text-[10px] uppercase tracking-widest text-enf-text-muted bg-enf-surface-mid border border-enf-surface-border px-1.5 py-0.5 rounded">Affiliate</span>
          </div>
          <div className="font-display text-xl tracking-wide leading-tight">{group.title}</div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-enf-text-muted" /> : <ChevronDown className="w-4 h-4 text-enf-text-muted" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-enf-surface-border">
          {group.blurb && <p className="text-sm text-enf-text-secondary mt-3 mb-4">{group.blurb}</p>}
          <div className="flex flex-col gap-2">
            {group.items.map((item) => {
              const isUrgent = !!item.urgent;
              const hasSubs = Array.isArray(item.subItems) && item.subItems.length > 0;
              const subOpen = !!expandedItems[item.id];

              return (
                <div
                  key={item.id}
                  className={`rounded-lg border transition-colors overflow-hidden ${
                    isUrgent
                      ? 'border-enf-accent-fg/50 bg-enf-accent/10'
                      : 'border-enf-surface-border bg-enf-surface-mid'
                  }`}
                >
                  {/* Main item row */}
                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {isUrgent && <Flame className="w-3.5 h-3.5 text-enf-accent-fg flex-shrink-0 scale-x-[-1]" />}
                        <span className={`font-bold text-sm ${isUrgent ? 'text-enf-accent-fg' : 'text-enf-text-primary'}`}>
                          {item.name}
                        </span>
                        {isUrgent && (
                          <span className="text-[9px] uppercase tracking-widest font-bold text-enf-accent-fg bg-enf-accent/20 border border-enf-accent-fg/30 px-1.5 py-0.5 rounded flex-shrink-0">
                            Start here
                          </span>
                        )}
                      </div>
                      {/* Link icon — only shown when there are no sub-items (direct link) */}
                      {!hasSubs && (
                        <a href={item.url || '#'} target="_blank" rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-enf-text-muted hover:text-enf-accent-fg transition-colors flex-shrink-0 mt-0.5">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                    {item.priceRange && (
                      <div className={`text-xs font-mono mb-1.5 ${isUrgent ? 'text-enf-accent-fg/80' : 'text-enf-role-info'}`}>
                        {item.priceRange}
                      </div>
                    )}
                    {item.why && <div className="text-xs text-enf-text-secondary leading-relaxed">{item.why}</div>}

                    {/* Sub-item toggle */}
                    {hasSubs && (
                      <button
                        type="button"
                        onClick={() => toggleItem(item.id)}
                        className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold transition-colors ${
                          isUrgent ? 'text-enf-accent-fg hover:text-enf-accent-fg/80' : 'text-enf-role-info hover:text-enf-text-primary'
                        }`}
                      >
                        {subOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        {subOpen ? 'Hide options' : `See ${item.subItems.length} options`}
                      </button>
                    )}
                  </div>

                  {/* Sub-items accordion */}
                  {hasSubs && subOpen && (
                    <div className="border-t border-enf-surface-border bg-enf-surface-deep divide-y divide-enf-surface-border">
                      {item.subItems.map((sub) => (
                        <a
                          key={sub.id}
                          href={sub.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-start gap-3 px-4 py-3 hover:bg-enf-surface-mid/60 transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-0.5">
                              <span className="font-semibold text-xs text-enf-text-primary group-hover:text-enf-accent-fg transition-colors">
                                {sub.name}
                              </span>
                              <ExternalLink className="w-3 h-3 text-enf-text-muted group-hover:text-enf-accent-fg transition-colors flex-shrink-0" />
                            </div>
                            {sub.priceRange && (
                              <div className="text-[10px] font-mono text-enf-role-info mb-1">{sub.priceRange}</div>
                            )}
                            {sub.why && (
                              <div className="text-[11px] text-enf-text-muted leading-relaxed">{sub.why}</div>
                            )}
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 text-[10px] text-enf-text-muted italic">
            Enferno earns a small commission when you purchase through these links — at no extra cost to you. Proceeds support the IronLimb mission.
          </div>
        </div>
      )}
    </div>
  );
};

export default GearRecommendations;
